import React, { useState, useRef, useEffect } from "react";
import { ScrollSync, ScrollSyncPane } from "react-scroll-sync";
import { FaUpload, FaCopy, FaFileExport, FaArrowUp, FaArrowDown } from "react-icons/fa";
import { ClipLoader } from "react-spinners";
import { FixedSizeList as List } from "react-window";

const JSONFormatter = () => {
  const [input, setInput] = useState("");
  const [output, setOutput] = useState([]);
  const [error, setError] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [showLineNumbers, setShowLineNumbers] = useState(true);
  const [tabSpace, setTabSpace] = useState(2); // Default tab space for formatting
  const [lineNumbers, setLineNumbers] = useState([]);

  const textareaRef = useRef(null);
  const lineNumbersRef = useRef(null);
  const outputListRef = useRef(null);

  const exampleData = `{
    "company": "SFDC247",
    "employees": [
      {
        "id": 1,
        "name": "John Doe",
        "role": "Salesforce Developer",
        "skills": ["Apex", "Visualforce", "LWC", "JavaScript"],
        "details": {
          "years_of_experience": 5,
          "certifications": ["Admin", "Platform Developer I", "Platform Developer II"]
        }
      },
      {
        "id": 2,
        "name": "Jane Smith",
        "role": "Salesforce Architect",
        "skills": ["Integration", "API Design", "Data Modeling"],
        "details": {
          "years_of_experience": 10,
          "certifications": ["System Architect", "Application Architect"]
        }
      }
    ]
  }`;

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError("File size exceeds 10 MB limit.");
        event.target.value = ""; // Reset the file input to allow reuploading the same file
        return;
      }
      const reader = new FileReader();
      setProcessing(true);
      reader.onload = (e) => {
        const fileContent = e.target.result;
        setInput(fileContent);
        setShowLineNumbers(true);
        setLineNumbers(fileContent.split("\n").map((_, index) => index + 1));
        setProcessing(false);
        setError("");
        event.target.value = ""; // Reset the file input to allow reuploading the same file
      };
      reader.onerror = () => {
        setError("Error reading the file. Please try again.");
        setProcessing(false);
        event.target.value = ""; // Reset the file input to allow reuploading the same file
      };
      reader.readAsText(file);
    }
  };

  const handleFormat = () => {
    try {
      setProcessing(true);
      setTimeout(() => {
        const parsedJSON = JSON.parse(input);
        const formattedJSON = JSON.stringify(parsedJSON, null, parseInt(tabSpace));
        setOutput(formattedJSON.split("\n")); // Split for lazy rendering
        setError("");
        setCopySuccess(false);
        setProcessing(false);
      }, 500);
    } catch (err) {
      setError("Invalid JSON. Please check your input.");
      setOutput([]);
      setCopySuccess(false);
      setProcessing(false);
    }
  };

  const handleClear = () => {
    setInput("");
    setOutput([]);
    setError("");
    setCopySuccess(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(output.join("\n"));
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const handleExport = () => {
    const blob = new Blob([output.join("\n")], { type: "application/json" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "formatted.json";
    link.click();
  };

  const scrollToTop = () => {
    if (textareaRef.current) textareaRef.current.scrollTop = 0;
    if (lineNumbersRef.current) lineNumbersRef.current.scrollTop = 0;
    if (outputListRef.current) outputListRef.current.scrollToItem(0); // Scroll to the first item
  };

  const scrollToBottom = () => {
    if (textareaRef.current) textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
    if (lineNumbersRef.current) lineNumbersRef.current.scrollTop = lineNumbersRef.current.scrollHeight;
    if (outputListRef.current) outputListRef.current.scrollToItem(output.length - 1); // Scroll to the last item
  };

  useEffect(() => {
    if (showLineNumbers) {
      setLineNumbers(input.split("\n").map((_, index) => index + 1));
    }
  }, [input, showLineNumbers]);

  const renderInputWithLineNumbers = () => (
    <ScrollSync>
      <div style={{ display: "flex", position: "relative", height: "300px" }}>
        {showLineNumbers && (
          <ScrollSyncPane>
            <div
              ref={lineNumbersRef}
              style={{
                overflowY: "auto",
                backgroundColor: "#f8f9fa",
                borderRight: "1px solid #ccc",
                textAlign: "right",
                userSelect: "none",
                color: "#6c757d",
                padding: "5px",
                width: "40px",
                boxSizing: "border-box",
                height: "100%",
              }}
            >
              {lineNumbers.map((number) => (
                <div key={number} style={{ height: "1.5em", lineHeight: "1.5em" }}>
                  {number}
                </div>
              ))}
            </div>
          </ScrollSyncPane>
        )}
        <ScrollSyncPane>
          <textarea
            ref={textareaRef}
            className="form-control"
            style={{
              flex: "1",
              height: "100%",
              resize: "none",
              border: error ? "2px solid red" : "2px solid #ccc",
              borderRadius: "0",
              overflowY: "auto",
              paddingLeft: showLineNumbers ? "0.5em" : "1em",
              fontFamily: "monospace",
            }}
            value={input}
            onScroll={(e) => {
              if (lineNumbersRef.current) lineNumbersRef.current.scrollTop = e.target.scrollTop;
            }}
            onChange={(e) => {
              setInput(e.target.value);
              setShowLineNumbers(true);
            }}
            placeholder="Enter raw JSON here..."
          />
        </ScrollSyncPane>
        <label
          htmlFor="upload-file"
          style={{ position: "absolute", top: "10px", right: "10px", cursor: "pointer" }}
        >
          <FaUpload size={20} />
        </label>
        <input
          type="file"
          id="upload-file"
          style={{ display: "none" }}
          accept=".json"
          onChange={handleFileUpload}
        />
      </div>
    </ScrollSync>
  );

  const renderLazyOutput = () => (
    <List
      height={300}
      itemCount={output.length}
      itemSize={20}
      width="100%"
      ref={outputListRef} // Attach the ref for scrolling
      style={{
        backgroundColor: "#f8f9fa",
        border: "2px solid #ccc",
        borderRadius: "5px",
        padding: "10px",
      }}
    >
      {({ index, style }) => (
        <div style={{ ...style, whiteSpace: "pre-wrap", fontFamily: "monospace" }}>{output[index]}</div>
      )}
    </List>
  );

  return (
    <div className="container mt-4">
      <div className="text-center mb-3">
        <button className="btn btn-info me-2" onClick={() => setInput(exampleData)}>
          Use Example Data
        </button>
        <select
          className="form-select d-inline-block w-auto"
          value={tabSpace}
          onChange={(e) => setTabSpace(e.target.value)}
        >
          <option value="2">2 Tab Space</option>
          <option value="3">3 Tab Space</option>
          <option value="4">4 Tab Space</option>
          <option value="8">8 Tab Space</option>
        </select>
      </div>
      {processing && <ClipLoader color="#007bff" size={50} />}
      {error && <div className="alert alert-danger text-center mt-3">{error}</div>}
      <div className="row">
        <div className="col-md-12 mb-3">{renderInputWithLineNumbers()}</div>
      </div>
      <div className="text-center mb-3">
        <button className="btn btn-success me-2" onClick={handleFormat} disabled={processing}>
          {processing ? "Processing..." : "Format JSON"}
        </button>
        <button className="btn btn-secondary me-2" onClick={handleClear}>
          Clear
        </button>
        <label>
          <input
            type="checkbox"
            checked={showLineNumbers}
            onChange={(e) => setShowLineNumbers(e.target.checked)}
            className="me-2"
          />
          Show Line Numbers
        </label>
      </div>
      {output.length > 0 && (
        <div className="mt-4">
          <h5>Formatted JSON</h5>
          <div>{renderLazyOutput()}</div>
          <div className="text-center mt-3">
            <button className="btn btn-secondary me-2" onClick={scrollToTop}>
              <FaArrowUp /> Scroll to Top
            </button>
            <button className="btn btn-secondary me-2" onClick={scrollToBottom}>
              <FaArrowDown /> Scroll to Bottom
            </button>
            <button
              className={`btn ${copySuccess ? "btn-success" : "btn-primary"} me-2`}
              onClick={handleCopy}
            >
              {copySuccess ? "Copied!" : "Copy Output"}
            </button>
            <button className="btn btn-warning ms-2" onClick={handleExport}>
              <FaFileExport /> Export JSON
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default JSONFormatter;